<template>
  <span ref="popover_wrapper" class="gs-popover--wrapper">
    <slot
      name="activator"
      :on="toggleable ? onClickListeners : onHoverListeners"
      :onHover_prevent="onReverseHoverListeners"
      :close="closePopover"
      :open="openPopover"
    ></slot>
    <mounting-portal mountTo="#app" :to="componentUid" transition="CustomTransition" append slim>
      <div
        v-if="isPopoverVisible"
        @mouseleave="$event => (persistOnHover && !toggleable ? closePopover($event) : undefined)"
        ref="gs-popover"
        class="gs-popover"
      >
        <div ref="gs-popover--arrow" class="gs-popover--arrow gs-popover--arrow__visible"></div>
        <div ref="gs-popover--arrow__ghost" class="gs-popover--arrow"></div>
        <div ref="gs-popover--content" class="gs-popover--content">
          <slot></slot>
        </div>
      </div>
    </mounting-portal>
  </span>
</template>

<script>
import { autoUpdate, computePosition, flip, shift, offset, arrow } from '@floating-ui/dom';

export default {
  name: 'PopoverMenu',
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    delay: {
      type: Number,
      default: null,
    },
    toggleable: {
      type: Boolean,
      default: false,
    },
    persistOnHover: {
      type: Boolean,
      default: false,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isPopoverVisible: false,
  }),
  watch: {
    isPopoverVisible: {
      async handler(isVisible, previousValue) {
        await this.$nextTick();
        if (previousValue !== undefined) this.$emit('change', isVisible);
        if (isVisible) {
          this.$emit('open');
          this.updatePopover();
          if (this.closeOnClickOutside)
            document.addEventListener('click', this.clickOutsideHandler, { once: true, capture: true });
        } else {
          this.$emit('close');
          this.cleanupAutoupdate?.();
        }
      },
      immediate: true,
    },
  },
  computed: {
    componentUid() {
      return '' + this._uid;
    },
    onClickListeners() {
      return { click: e => this.togglePopover(e) };
    },
    onHoverListeners() {
      return {
        mouseenter: this.openPopover,
        mouseleave: this.closePopover,
      };
    },
    /** Especially useful when used as 'prevent' for child hovering */
    onReverseHoverListeners() {
      return {
        mouseenter: this.closePopover,
        mouseleave: this.openPopover,
      };
    },
  },
  methods: {
    clickOutsideHandler(e) {
      if (this.$refs['gs-popover--content']?.contains(e.target) || this.$refs['gs-popover--content'] === e.target) {
        document.addEventListener('click', this.clickOutsideHandler, { once: true, capture: true });
        return;
      }
      e.stopPropagation();
      this.closePopover();
    },
    togglePopover(e) {
      e.stopPropagation();
      this.isPopoverVisible = !this.isPopoverVisible;
    },
    openPopover() {
      if (!this.delay) return (this.isPopoverVisible = true);
      this.timeout = setTimeout(() => (this.isPopoverVisible = true), this.delay);
    },
    closePopover(e) {
      clearTimeout(this.timeout);
      this.timeout = null;
      if (!this.toggleable && this.persistOnHover) {
        const isTargetPopover = e?.relatedTarget?.classList.contains('gs-popover');
        const isTargetActivator = e?.relatedTarget?.parentElement?.classList.contains('gs-popover--wrapper');
        if (!isTargetPopover && !isTargetActivator) {
          this.isPopoverVisible = false;
        }
      } else this.isPopoverVisible = false;
    },
    updatePopover() {
      const popoverElement = this.$refs['gs-popover'];
      const popoverArrowElement = this.$refs['gs-popover--arrow'];
      const popoverArrowShadowElement = this.$refs['gs-popover--arrow__ghost'];
      const triggerElement = this.$children[0].$el;
      this.cleanupAutoupdate = autoUpdate(triggerElement, popoverElement, () =>
        computePosition(triggerElement, popoverElement, {
          middleware: [offset(10), shift({ padding: 10 }), flip(), arrow({ element: popoverArrowElement })],
          placement: 'right-start',
          ...this.config,
        }).then(({ x, y, placement, middlewareData }) => {
          /** Popover placement */
          Object.assign(popoverElement.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
          const placementSide = placement.split('-')[0];

          /** Arrow placement */
          const { x: arrowX, y: arrowY } = middlewareData.arrow;
          const staticSide = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
          }[placementSide];
          [popoverArrowElement, popoverArrowShadowElement].forEach(el =>
            Object.assign(el.style, {
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              right: '',
              bottom: '',
              [staticSide]: '-4px',
            })
          );
        })
      );
    },
  },
  beforeDestroy() {
    this.cleanupAutoupdate?.();
  },
};
</script>

<style lang="scss" scoped>
$arrowSide: 11px;
$arrowDiagonal: calc((#{$arrowSide} * (2 * (1 / 2))) + 3px);

.gs-popover::before {
  content: '';
  top: calc(#{$arrowDiagonal} * -1);
  left: calc(#{$arrowDiagonal} * -1);
  height: calc(100% + calc(#{$arrowDiagonal} * 2));
  width: calc(100% + calc(#{$arrowDiagonal} * 2));
  background-color: transparent;
  position: absolute;
  z-index: -9999;
}

.gs-popover--arrow {
  position: absolute;
  width: $arrowSide;
  height: $arrowSide;
  background: #fff;
  transform: rotate(45deg);
}
</style>

<style scoped>
@reference '@/tailwind.css';

.gs-popover {
  @apply tw:w-max! tw:absolute! tw:z-9999!;
}

.gs-popover--content {
  @apply tw:bg-white! tw:border! tw:border-gray-200! tw:shadow-md! tw:rounded-base! tw:p-4!;
}

.gs-popover--arrow__visible {
  @apply tw:shadow-md! tw:outline! tw:outline-gray-200! tw:z-[-9999]!;
}

/* Fade transition */

.transition-enter-active,
.transition-leave-active {
  transition: opacity 200ms;
}
.transition-enter,
.transition-leave-to {
  @apply tw:opacity-0!;
}

.transition-enter-to,
.transition-leave {
  @apply tw:opacity-100!;
}
</style>
